import { Injectable } from '@angular/core';
import { Subscription, Observable } from 'rxjs';

import { ApiModel } from '../api.model';
import { S3FileModel, S3File } from '../s3File.model';
import { Brand, BrandFromGetAll } from './types';
import type { Folder, AttachmentRequestBody } from './types';

@Injectable()
export class BrandModel {
    private marketingFilesSubscription: Subscription;

    constructor(private apiModel: ApiModel, private s3FileModel: S3FileModel) {}

    public getOrganizationBrandsVisibilityFile() {
        return this.s3FileModel.getJSON(S3File.UserBrandVisibility).toPromise();
    }

    public getBrand(brandId: string) {
        return this.apiModel.makeHttpRequest({
            method: 'GET',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}`
        });
    }

    public getBrands(params: Partial<GetBrandsParams> = {}): Observable<BrandFromGetAll[]> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'GET',
            params,
            url: `${this.apiModel.getBrandsEndpoint()}`
        });
    }

    public getBrandsForRetailers(): Promise<Brand[]> {
        return this.apiModel.makeHttpRequest({
            method: 'GET',
            url: this.apiModel.getBrandsEndpoint()
        });
    }

    public getMarketingFilesOneRequest(brandId: string) {
        return new Promise((resolve, reject) => {
            if (this.marketingFilesSubscription) {
                this.marketingFilesSubscription.unsubscribe();
            }

            this.marketingFilesSubscription = this.apiModel
                .makeHttpObservableRequest({
                    method: 'GET',
                    url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/marketing-files`
                })
                .subscribe((response) => resolve(response), reject);
        });
    }

    public getRetailerProfile({
        brandId,
        retailerId,
        accessRequestId
    }: {
        brandId: string;
        retailerId: string;
        accessRequestId?: string;
    }) {
        return this.apiModel.makeHttpObservableRequest({
            method: 'GET',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/retailerProfile`,
            params: {
                retailerId,
                ...(accessRequestId ? { accessRequestId } : {})
            }
        });
    }

    public addBrand(data: any): Promise<Brand> {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getBrandsEndpoint()}`,
            body: data
        });
    }

    public updateBrand(brandId: string, data: any): Promise<Brand> {
        return this.apiModel.makeHttpRequest({
            method: 'PUT',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}`,
            body: data
        });
    }

    public uploadAttachment(brandId: string, data: AttachmentRequestBody) {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/attachment`,
            body: data
        });
    }

    public updateCategories(brandId: string, categories) {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/categories`,
            body: categories
        });
    }

    public updateFolders(brandId: string, folders: Folder[]) {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/folders`,
            body: folders
        });
    }

    public updateFolder(brandId: string, folder: Folder) {
        return this.apiModel.makeHttpRequest({
            method: 'PUT',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/folders`,
            body: folder
        });
    }

    public updatedSortedBrandFolders(brandId: string, folders: Folder[]) {
        return this.apiModel.makeHttpRequest({
            method: 'PUT',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/folders/sorted`,
            body: folders
        });
    }

    public requestMarketingFile(brandId: string, requestData: any) {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/requestMarketingFile`,
            body: requestData
        });
    }

    public deleteBrand(brandId: string) {
        return this.apiModel.makeHttpRequest({
            method: 'DELETE',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}`
        });
    }

    public addRetailerGroup(brandId: string, requestData: any) {
        return this.apiModel.makeHttpRequest({
            method: 'POST',
            url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/retailer-groups`,
            body: requestData
        });
    }
}

type GetBrandsParams = { participates: 'preorderPlatform' };
